import { User } from "@prisma/client";
import { IComment } from "immigram-ui-cmon/src/components/Comments/types";
import { showNotification } from "immigram-ui-cmon/src/components/Notification/Notification";
import { useTranslation } from "next-i18next";
import React, { useCallback, useMemo } from "react";

import { getApplicationSection } from "@/lib/application";
import { ApplicationSectionStatusType } from "@/lib/applicationSectionStatus";
import { isAdmin } from "@/lib/auth/rbac";
import { ApplicationFull } from "@/lib/prisma";
import { ApplicationFormPrepared } from "@/lib/prisma";

import { showConfirmModal } from "@/components/Modal/ModalController";
import ProfessionalJourneyCompany from "@/components/ProfessionalJourneyCompany/ProfessionalJourneyCompany";
import { CareerRole } from "@/components/ProfileExperience/lib/getCompanyFields";

import { ProfJourneyFormNamespace } from "../..";

export enum ProfJourneyCompanyListMode {
  Potential = "potential",
  Details = "details",
}

interface ProfJourneyCompanyListProps {
  experience?: ApplicationFormPrepared[];
  ownUser: User;
  editable?: boolean;
  sectionStatus?: ApplicationSectionStatusType;
  mode: ProfJourneyCompanyListMode;
  comments?: IComment[];
  application: ApplicationFull;
  showErrors?: boolean;
  onGenerateDoc?: (companyData: ApplicationFormPrepared) => Promise<void>;
  onGenerateChecklist?: (companyData: ApplicationFormPrepared) => Promise<void>;
  onCompanyDelete?: ({
    companyData,
  }: {
    companyData: ApplicationFormPrepared;
  }) => Promise<void>;
  onCompanyClick?: ({
    companyData,
  }: {
    companyData: ApplicationFormPrepared;
  }) => void;
  onProjectCreate?: ({
    companyData,
  }: {
    companyData: ApplicationFormPrepared;
  }) => void;
  onProjectClick?: ({
    companyData,
    projectData,
  }: {
    projectData: ApplicationFormPrepared;
    companyData: ApplicationFormPrepared;
  }) => void;
  onProjectCheckboxChange?: ({
    companyData,
    checked,
    projectData,
  }: {
    checked: boolean;
    projectData: ApplicationFormPrepared;
    companyData: ApplicationFormPrepared;
  }) => void;
}

export function ProfJourneyCompanyList({
  experience = [],
  ownUser,
  editable,
  sectionStatus,
  mode,
  comments,
  application,
  showErrors,
  onGenerateDoc,
  onGenerateChecklist,
  onCompanyClick,
  onCompanyDelete,
  onProjectCreate,
  onProjectClick,
  onProjectCheckboxChange,
}: ProfJourneyCompanyListProps) {
  const { t } = useTranslation();

  const isDetailsMode = mode === ProfJourneyCompanyListMode.Details;

  const handleCompanyDelete = useCallback(
    ({ companyData }: { companyData: ApplicationFormPrepared }) => {
      showConfirmModal({
        heading: t("application:professionalJourneyCompany.deleteHeading", {
          name: companyData?.fieldsMap?.name?.value,
        }),
        text: t("application:professionalJourneyCompany.deleteText"),
        confirmButtonText: t("application:professionalJourneyCompany.delete"),
        onConfirm: async () => {
          try {
            await onCompanyDelete?.({ companyData });
          } catch (e) {
            showNotification({
              variant: "alert",
              title: t(
                "application:professionalJourneyCompany.notification.failedToDelete"
              ),
            });
          }
        },
      });
    },
    [onCompanyDelete, t]
  );

  const companyListData = useMemo(() => {
    const commentsCountByProject = (comments || [])
      .filter((item) => !item.isResolved && !item.isDraft)
      .reduce((acc: Record<string, number>, comment) => {
        const project = comment.group;

        if (!project) {
          return acc;
        }

        if (!acc[project]) {
          acc[project] = 1;
        } else {
          acc[project]++;
        }

        return acc;
      }, {});

    return experience.map((exp) => {
      const companyName = exp.fieldsMap?.name?.value;
      const dateFrom = exp.fieldsMap?.dateFrom?.value;
      const dateTo = exp.fieldsMap?.dateTo?.value;
      const careerRoleId = exp.fieldsMap?.careerRoleId?.value;
      const workingNow = exp.fieldsMap?.workingNow?.value === "true";

      const position = careerRoleId && CareerRole[Number(careerRoleId)];

      let projects = exp.children;

      if (isDetailsMode) {
        projects = projects?.filter((child) =>
          isDetailsMode
            ? child.statusId === ApplicationSectionStatusType.ReviewCompleted
            : true
        );
      }

      const projectsPrepared = projects
        ?.filter((project) =>
          [
            ProfJourneyFormNamespace.ProjectEmployment,
            ProfJourneyFormNamespace.ProjectEntrepreneurship,
          ].includes(project.namespace as ProfJourneyFormNamespace)
        )
        ?.map((project) => {
          const projectName = project.fieldsMap?.name?.value;
          const detailsForm = project.children?.find(
            (child) =>
              child.namespace &&
              [
                ProfJourneyFormNamespace.ProjectDetailsEmployment,
                ProfJourneyFormNamespace.ProjectDetailsEntrepreneurship,
              ].includes(child.namespace as ProfJourneyFormNamespace)
          );

          return {
            id: isDetailsMode ? detailsForm?.id : project.id,
            name: projectName,
            status: isDetailsMode ? detailsForm?.statusId : project.statusId,
            hasUnresolvedComments:
              isDetailsMode &&
              Boolean(detailsForm && commentsCountByProject[detailsForm.id]),
            projectData: project,
            isValid: isDetailsMode ? detailsForm?.isValid : project.isValid,
          };
        });

      return {
        companyData: exp,
        companyName,
        yearFrom: dateFrom && String(new Date(dateFrom).getFullYear()),
        yearTo: workingNow
          ? t("application:getExperienceYears.text.now")
          : dateTo && String(new Date(dateTo).getFullYear()),
        position,
        projects: projectsPrepared,
      };
    });
  }, [experience, isDetailsMode, comments, t]);

  const isCheckbox =
    isAdmin(ownUser) &&
    (sectionStatus === ApplicationSectionStatusType.SubmittedForReview ||
      sectionStatus === ApplicationSectionStatusType.ReviewCompleted);

  return (
    <>
      <div className="flex flex-col gap-5">
        {companyListData.map((item) => {
          if (
            mode === ProfJourneyCompanyListMode.Details &&
            !item.projects?.length
          ) {
            return null;
          }

          return (
            <ProfessionalJourneyCompany<
              ApplicationFormPrepared,
              ApplicationFormPrepared
            >
              id={item.companyData.id}
              key={item.companyData.id}
              companyData={item.companyData}
              companyName={item.companyName}
              yearFrom={item.yearFrom}
              yearTo={item.yearTo}
              position={item.position}
              projects={item.projects}
              checkbox={isCheckbox}
              showErrors={showErrors}
              ownUser={ownUser}
              sectionStatus={isDetailsMode ? undefined : sectionStatus}
              editable={editable}
              onDelete={handleCompanyDelete}
              onClick={onCompanyClick}
              onProjectCreate={onProjectCreate}
              onProjectClick={onProjectClick}
              onProjectCheckboxChange={onProjectCheckboxChange}
              onGenerateDoc={onGenerateDoc}
              onGenerateChecklist={onGenerateChecklist}
              application={application}
              showProvideDocsButton={
                isDetailsMode &&
                getApplicationSection(application)?.isProvideDocsUnlocked
              }
            />
          );
        })}
      </div>
    </>
  );
}
